import { useMediaQuery, Box, Button, Card, Checkbox, CircularProgress, Divider, InputAdornment, TextField, FormControl, FormControlLabel, FormGroup, Grid, Popover, Radio, RadioGroup, Stack, SvgIcon, Typography, Drawer, IconButton, } from '@mui/material'
import React, { useRef, useState, useEffect, useCallback } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import PropTypes from 'prop-types';
import { jobDetails } from 'api/job';
import { useAuth } from 'hooks/useAuthContext';
import { getTimeDifferenceString, formatNumber } from 'utils/Utils';
import { SplashScreen } from 'components/loading-screen';
import { saveJobApplication } from 'api/application';
import { useSnackbar } from 'components/snackbar';
import { Link, useNavigate, useParams } from 'react-router-dom';
import JobDetails from './JobDetails';
import JobDescription from './JobDescription';
import { getJobsForExternalUse } from 'api/job';
import { debounce } from 'lodash';
import { getJobPreferanceDetails } from 'api/users';
import EmptyState from './EmptyState';
import { clusteredData } from 'data/city.js';
import { useTheme } from '@mui/material/styles';
import JobDetailsMobile from './JobDetailsMobile';
import JobTypeToggle from './JobTypeToggle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

const ALL_JOB_TYPES = ['Full time', 'Part time', 'Temporary', 'Internship'];
const PART_TIME_TYPES = ['Part time', 'Temporary', 'Internship'];

/**
 * A component that renders a tab panel for a tabbed interface.
 * It displays its children content only when the current tab index matches the panel's index.
 *
 * @param {object} props - The properties passed to the component.
 * @param {*} props.children - The content to be displayed inside the tab panel.
 * @param {number} props.value - The current active tab index.
 * @param {number} props.index - The index of this tab panel.
 * @param {object} [props.other] - Any other props to apply to the component.
 */

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

/**
 * Generates accessibility properties for a tab component.
 *
 * @param {number} index - The index of the tab.
 * @returns {object} An object containing the id and aria-controls attributes 
 * for the tab and its associated tab panel.
 */
function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

/**
 * InternalJobFromExternal component handles the display and interaction
 * logic for job listings. It manages state related to job filters, search,
 * and user preferences. The component fetches job data based on filter and
 * search criteria and displays the list of jobs, allowing users to view job
 * details, apply filters, and save jobs. It also handles navigation and user
 * authentication context to enhance user experience.
 */

const InternalJobFromExternal = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const [userId, setUserId] = useState(user?.id || null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { id } = useParams();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { jobApply, setJobApply } = useAuth();
  const [setActiveId, setSetActiveId] = useState(null);

  const [filterApplied, setFilterApplied] = useState(false);
  const [showSearch, setShowSearch] = React.useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortCriteria, setSortCriteria] = useState({});
  const [sortValue, setSortValue] = useState(null);
  const [preferedLocation, setPreferedLocation] = useState([]);
  // const jobTypes = ['Full time', 'Part time', 'Temporary', 'Internship'];
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterCriteria, setFilterCriteria] = useState({
    location: [],
    work_format: [],
    job_type: []
  });

  // const open = Boolean(anchorEl);
  const [value, setValue] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const inputRef = useRef(null);
  const [applyButton, setApplyButton] = useState(false);
  const [isJobPresent, setIsJobPresent] = useState(false);
  const cardRefs = useRef({});
  const scrollContainerRef = useRef(null);
  // const [openDetailsDrawer, setOpenDetailsDrawer] = useState(id ? true : false);
  const [jobTypes, setJobTypes] = useState([]);
  // const [shouldReset, setShouldReset] = useState(false);
  const [jobID, setJobID] = useState(null);

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const openDetailsDrawer = dialogIds.includes("ExternalJobDetails");
  const openFilter = dialogIds.includes("jobFilterDrawer");

  const handleCloseDetailsDrawer = () => {
    closeDialog('ExternalJobDetails');
  };

  useEffect(() => {
    /**
     * Fetches job data based on filter and search criteria and displays the
     * list of jobs. It also handles navigation and user authentication context
     * to enhance user experience.
     */
    const fetchData = async () => {
      await fetchJob();
    };
    fetchData();
  }, []);

  useEffect(() => {
    setUserId(user?.id || null);
    /**
     * Asynchronously fetches job preferences for the current user.
     * This function checks if a user is authenticated and then calls
     * the fetchJobPreferences function to retrieve and update the user's
     * job preference data.
     */

    const fetchPreferences = async () => {
      if (user) {
        await fetchJobPreferences();
      }
    };
    fetchPreferences();
  }, []);
  useEffect(() => {
    if (setActiveId && cardRefs.current[setActiveId]) {
      scrollContainerRef.current.scrollTo({
        top: cardRefs.current[setActiveId].offsetTop - scrollContainerRef.current.clientHeight / 2 + cardRefs.current[setActiveId].offsetHeight / 2,
        behavior: 'smooth',
      });
    }
  }, [setActiveId, loading]);


  /**
   * Fetches job data based on filter and search criteria and displays the
   * list of jobs. It also handles navigation and user authentication context
   * to enhance user experience.
   * @param {string} search - The search query to filter the jobs.
   * @param {object} filters - The filter criteria to filter the jobs.
   * @param {boolean} reload - Whether to reload the job list or not.
   * @returns {Promise<void>}
   */
  const fetchJob = async (search, filters, reload = false) => {
    let selectedId = null;
    let job = null;
    try {
      const response = await getJobsForExternalUse({ search, filters, sort: sortCriteria });
      if (response && response?.success && response?.data && response?.data?.job) {
        setJobs(response?.data?.job);
        setIsJobPresent(response.data?.isJobPresent);
        if (reload) {
          selectedId = response?.data?.job[0]?._id;
          job = response?.data?.job[0];
        } else if (jobApply?.job?._id) {
          const selectedJob = response?.data?.job.find((job) => job._id === jobApply?.job?._id);
          selectedId = selectedJob?._id || response?.data?.job[0]?._id;
          job = selectedJob || response?.data?.job[0];
        } else if (id) {
          const highlightedJob = response?.data?.job.find((job) => job.jobid === id);
          selectedId = highlightedJob?._id || response?.data?.job[0]?._id;
          job = highlightedJob || response?.data?.job[0];
          if (isMobile) openDialog('ExternalJobDetails');
        } else {
          selectedId = response?.data?.job[0]?._id;
          job = response?.data?.job[0];
        }
      } else {
        setJobs([]);
      }
      setSetActiveId(selectedId);
      setJobID(job?.jobid);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setFilterLoading(false);
      if (user) await fetchJobDetails(selectedId);
    }
  };

  /**
   * Asynchronously fetches the job preferences for the user.
   * Retrieves preferred job locations from the server and updates
   * the local state with filtered locations, excluding "Any".
   * Logs an error message if the fetch operation fails.
   */

  const fetchJobPreferences = async () => {
    try {
      const response = await getJobPreferanceDetails();
      if (response?.success && response.data) {
        const location = response.data.prefered_location.filter((loc) => loc !== "Any");
        setPreferedLocation(location || []);
      } else {
        console.error('Failed to fetch job preferences', response?.message);
      }
    } catch (error) {
      console.error('Error fetching job preferences:', error);
    }
  };

  /**
   * Handles the event when the user applies the selected filter criteria.
   * Checks if any filter criteria is selected, sets the filter loading state to true,
   * sets the filter applied flag to true, fetches the jobs based on the selected filter
   * criteria and search query, and closes the filter popover.
   */
  const handleApplyFilter = async () => {
    if (!sortCriteria && filterCriteria?.location?.length === 0 && filterCriteria?.work_format?.length === 0 && filterCriteria?.job_type?.length === 0) {
      return;
    }
    setFilterLoading(true);
    setFilterApplied(true);
    fetchJob(searchValue, filterCriteria, true);
    handlePopoverClose();
  };
  /**
   * Handles the event when the user clicks on a job.
   * Resets the jobApply state to null, sets the active job id to the selected job id,
   * opens the job details drawer, and resets the external job status.
   * @param {Object} job - The job that was clicked.
   */
  const handleJobData = (job) => {
    setJobApply(null);
    setSetActiveId(job?._id);
    setJobID(job?.jobid);
    if (isMobile) {
      openDialog('ExternalJobDetails');
    }
    // handleOpenDrawer(true);
    // setShowExternalJobStatus('');
  };
  /**
   * Handles the event when the user clicks on the filter criteria.
   * Sets the anchor element to the element that triggered the event.
   * @param {React.MouseEvent<HTMLDivElement>} event - The event that triggered the filter criteria open.
   */
  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
    openDialog('jobFilterDrawer');
  };
  /**
   * Handles the event when the user clicks on the search bar.
   * Sets the show search state to true, and sets the focus to the search input field
   * after a short delay.
   */
  const handleShowSearch = () => {
    setShowSearch(true);
    // setShowFilter(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 300);
  };
  /**
   * Handles the event when the user clicks on the close button for the popover.
   * Resets the anchor element to null, and if the user is cancelling the filter,
   * resets the filter criteria, sort criteria, and fetches the jobs without any
   * filters or sorting.
   * @param {boolean} isCancel - Whether the user is cancelling the filter.
   */
  const handlePopoverClose = (isCancel) => {
    closeDialog('jobFilterDrawer');
    setAnchorEl(null);
    const isFilterApplied = (filterCriteria?.location?.length || filterCriteria?.work_format?.length || filterCriteria?.job_type?.length);
    const isSortingApplied = Object?.keys(sortCriteria)?.length;
    if (isCancel && (isFilterApplied || isSortingApplied || filterApplied)) {
      setFilterApplied(false);
      setSortCriteria({});
      setFilterCriteria({
        location: [],
        work_format: [],
        job_type: []
      });
      fetchJob(searchValue, '', true);
      setFilterLoading(true);
    }
  };
  /**
   * Handles the change event for the tabs.
   * @param {React.ChangeEvent<HTMLElement> | null} event - The event that triggered the change.
   * @param {string} newValue - The new value of the tab.
   */
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  /**
   * Handles the change event for the sort criteria.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The event that triggered the change.
   * Updates the sort criteria state with the new value and name of the changed sort criteria.
   */
  const handleSortChange = (event) => {
    const { name, value } = event.target;
    setSortValue(value);
    if (value === '1' || value === '0') {
      setSortCriteria({ salary: value });
    } else {
      setSortCriteria({ updatedAt: value === '11' ? '1' : '0' });
    }
  };
  /**
   * Handles the double click event on the sort criteria chips.
   * If the same criteria is double clicked, it will be reset to an empty string.
   * @param {object} event - The event that triggered the double click.
   */
  const handleDoubleClick = (event) => {
    const { name, value } = event.target;
    if (value === '1' || value === '0') {
      setSortCriteria({ salary: value });
    } else {
      setSortCriteria({ updatedAt: value === '11' ? '1' : '0' });
    }
  };
  /**
   * Toggles the visibility of the search bar and resets the search value.
   * If a search value is present, it hides the search bar, clears the search input,
   * and reloads the job listings using the current filter criteria.
   * If no search value is present, it simply hides the search bar and reloads
   * the job listings using the current filter criteria.
   */

  const handleShowHide = async () => {
    if (searchValue) {
      setShowSearch(false);
      setSearchValue('');
      fetchJob('', filterCriteria, true);
    } else {
      setShowSearch(false);
      fetchJob('', filterCriteria, true);
    }
  };
  /**
   * Handles the change in the search input value by setting the search
   * value state and invoking the debounced search function with the new
   * value and filter criteria.
   * @param {string} value - The new search input value.
   */

  const handleSearchInputChange = (value) => {
    setSearchValue(value);
    // debouncedSearch(value, filterCriteria);
  };
  const debouncedSearch = useCallback(
    debounce(async (value, flc) => {
      setFilterLoading(true);
      await fetchJob(value, flc, true);
    }, 500),
    []
  );
  /**
   * Handles the change of the filter criteria. Updates the filterCriteria
   * state with the new value and name of the changed filter criteria.
   * @param {object} event - The event that triggered the change.
   * @param {string} value - The new value of the changed filter criteria.
   */
  const handleChangeFilterCriteria = (event, value) => {
    const { name } = event.target;
    setFilterCriteria((prevCriteria) => ({
      ...prevCriteria,
      [name]: prevCriteria[name].includes(value)
        ? prevCriteria[name].filter((item) => item !== value)
        : [...prevCriteria[name], value],
    }));
  };

  /**
   * Fetches job details for a given job ID.
   * Retrieves the job data, job application, and job status based on the selectedId.
   * If the response is successful, it extracts and assigns the relevant data.
   * If the response fails, it logs an error message.
   * Invokes handleNavigation with the retrieved data to handle further actions.
   * 
   * @param {string} selectedId - The ID of the job to fetch details for.
   * @returns {Promise<void>}
   */

  const fetchJobDetails = async (selectedId) => {
    if (!selectedId) return;
    let jobStatus;
    let jobData;
    let jobApplication;
    const response = await jobDetails({ id: selectedId, userId: user?.id });
    if (response?.success) {
      if (response?.data?.job) {
        jobData = response?.data?.job;
        jobApplication = response?.data?.jobIdInCandidate;
        jobStatus = response?.data?.jobIdInCandidate?.status;
      }
    } else console.error("Failed to fetch job info:", response.message);
    if (user?.user_type !== 'user') return navigate('/', { replace: true });
    handleNavigation(jobData, jobStatus, jobApplication);
  };
  /**
   * Handles navigation based on job application status.
   * If user is not logged in or hasn't completed profile setup, saves job application.
   * Otherwise, sets job apply state and navigates to job application page.
   * @param {object} jobData - Job data.
   * @param {string} jobStatus - Job application status.
   * @param {object} jobApplication - Job application data.
   */

  const handleNavigation = (jobData, jobStatus, jobApplication) => {
    let jobId = jobData?._id;
    const companyId = jobData?.company_id;
    const status = jobStatus || null;
    if (!user?.stages || user?.stages < 4) {
      if (jobId && companyId) handleSaveJob(jobId, companyId);
      if (jobId) localStorage.setItem('jobIdForNewUser', jobData?.jobid);
    } else {
      if ((status === 'saved' || status === null)) {
        console.log('job-market');
        if (status === 'saved') jobData.application_saved = status;
      } else {
        const jobDetails = jobApplication?.job_id;
        jobId = jobApplication?.job_id?._id;
        jobApplication.jobDetails = jobDetails;
        jobApplication.job_id = jobId;
      }
      if (isMobile && !openDetailsDrawer) {
        setJobApply({ tab: '2' });
      } else {
        setJobApply({
          job: ((status === 'saved' || status === null) ? jobData : jobApplication),
          // openChatBot: ((status === 'saved' || status === null || status === 'application pending') ? true : false),
          openChatBot: false,
          resume: null, answer: null,
          tab: ((status === 'saved' || status === null) ? '2' : '3'),
          activeTab: ((status === 'application pending') ? 'saved-jobs' : 'applied-jobs'),
          filter: id
        });
      }
    }
    localStorage.removeItem('jobIdForNavigation');
    navigate('/', { replace: true });
  }

  /**
   * Saves a job application for later by calling the saveJobApplication API.
   * @function
   * @async
   * @return {Promise<void>}
   */
  const handleSaveJob = async (jID, cId) => {
    try {
      setLoading(true);
      let jobId = jID;
      let companyId = cId;
      const response = await saveJobApplication({ jobId, companyId, type: 'saved' });
      if (response?.success && response?.data != null) {
        enqueueSnackbar(response?.message, { variant: 'success' });
      } else {
        // enqueueSnackbar('Failed to save the application. Please try again.', { variant: 'error' });
        console.error(response?.message, e);
      }
    } catch (e) {
      console.error('Error saving for later:', e);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Handles toggling of job details drawer.
   * @param {boolean} newOpen - True if the drawer should be open, false if it should be closed.
   * @return {void}
   */
  // const handleOpenDrawer = (newOpen) => {
  //   // setOpenDetailsDrawer(newOpen);
  //   newOpen ? handleOpenDetailsDrawer() : handleCloseDetailsDrawer();
  // };

  /**
   * Handles fetching of jobs based on job type filter.
   * @param {string} value - The selected job type filter value.
   * @return {Promise<void>}
   */
  const handleFetchJobFromJobType = async (value) => {
    if (filterCriteria?.location?.length === 0 && filterCriteria?.work_format?.length === 0 && Object?.keys(sortCriteria)?.length === 0) setFilterApplied(false);
    setFilterLoading(true);
    const jobTypeFilter =
      value === 'both'
        ? [...ALL_JOB_TYPES]
        : value === 'Full time'
          ? ['Full time']
          : [...PART_TIME_TYPES];
    const updatedFilter = { ...filterCriteria, job_type: jobTypeFilter };
    setFilterCriteria(updatedFilter);
    await fetchJob(searchValue, updatedFilter, true);
  };

  const getToggleAlignment = () => {
    const selected = new Set(filterCriteria.job_type);
    if (selected.size >= ALL_JOB_TYPES.length) return 'both';
    if (selected.size === 1 && selected.has('Full time')) return 'Full time';
    if (selected.size > 0 && [...selected].every(type => PART_TIME_TYPES.includes(type))) return 'Part time';
    return 'both';
  };

  const handleSearch = async (value, flc) => {
    setFilterLoading(true);
    await fetchJob(value, flc, true);
  };

  if (!jobs?.length && loading) {
    return (
      <Grid container justifyContent="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%', height: 'calc(100vh - 110px)' }} alignItems="center">
        <SplashScreen />
      </Grid>
    );
  }

  if (jobs.length === 0 && !isJobPresent) {
    return (
      <Box marginTop={5}>
        <EmptyState tab={'job-market'} />
      </Box>
    );
  }

  return (
    <Box className="job-full-details" sx={{ padding: '50px 10px 0px 10px !important', overflow: 'hidden' }} >
      <Grid container spacing={2} sx={{ backgroundColor: '#f1f5f7' }}>
        <Grid item xs={12} md={4}>
          <Box className="active-jobs-list">
            {/* header/filter */}
            <Box className="header-wrapper" >
              <Box className="filter-wrapper" mb={2}>
                <Stack direction="row" justifyContent={'space-between'} spacing={2}>
                  <Typography className="head-text" variant="p">
                    {jobs?.length > 0 ? `${jobs?.length} Active Jobs` : 'Result Not Found'}
                  </Typography>
                  {/* filter */}
                  <Box>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        aria-label="update"
                        aria-owns={openFilter || filterApplied ? 'filter-by' : undefined}
                        aria-haspopup="true"
                        onClick={handlePopoverOpen}
                      >
                        {filterApplied && <SvgIcon className="ellipse-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                            <circle cx="6" cy="6.5" r="6" fill="#C12929" />
                          </svg>
                        </SvgIcon>}
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="16" viewBox="0 0 18 16" fill="none">
                          <path file-rule="evenodd" clipRule="evenodd" d="M7.0923 12.3074H16.3846C16.7243 12.3074 17 12.0317 17 11.692C17 11.3523 16.7243 11.0766 16.3846 11.0766H7.0923C6.80677 9.67293 5.56431 8.61508 4.07692 8.61508C2.37846 8.61508 1 9.99355 1 11.692C1 13.3905 2.37846 14.7689 4.07692 14.7689C5.56431 14.7689 6.80677 13.7111 7.0923 12.3074ZM4.07692 9.84585C5.096 9.84585 5.92308 10.6729 5.92308 11.692C5.92308 12.7111 5.096 13.5382 4.07692 13.5382C3.05785 13.5382 2.23077 12.7111 2.23077 11.692C2.23077 10.6729 3.05785 9.84585 4.07692 9.84585ZM10.9077 3.69201H1.61538C1.27569 3.69201 1 3.9677 1 4.30739C1 4.64708 1.27569 4.92278 1.61538 4.92278H10.9077C11.1932 6.32647 12.4357 7.38432 13.9231 7.38432C15.6215 7.38432 17 6.00585 17 4.30739C17 2.60893 15.6215 1.23047 13.9231 1.23047C12.4357 1.23047 11.1932 2.28832 10.9077 3.69201ZM13.9231 2.46124C14.9422 2.46124 15.7692 3.28832 15.7692 4.30739C15.7692 5.32647 14.9422 6.15355 13.9231 6.15355C12.904 6.15355 12.0769 5.32647 12.0769 4.30739C12.0769 3.28832 12.904 2.46124 13.9231 2.46124Z" fill="#9CA3AF" stroke="white" strokeWidth="0.5" />
                        </svg>
                      </IconButton>
                      <IconButton className={showSearch ? "search-active" : ""} aria-label="update" onClick={handleShowSearch}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            file-rule="evenodd"
                            clipRule="evenodd"
                            d="M1.23254 7.20043C1.23254 3.9044 3.9045 1.23242 7.20052 1.23242C10.4965 1.23242 13.1685 3.9044 13.1685 7.20043C13.1685 10.4964 10.4965 13.1684 7.20052 13.1684C3.90449 13.1684 1.23254 10.4964 1.23254 7.20043ZM7.20052 0.232422C3.35221 0.232422 0.232544 3.35212 0.232544 7.20043C0.232544 11.0487 3.35221 14.1684 7.20052 14.1684C8.94474 14.1684 10.5393 13.5275 11.7615 12.4684L14.9139 15.6208C15.1092 15.8161 15.4258 15.8161 15.621 15.6208C15.8163 15.4255 15.8163 15.1089 15.621 14.9137L12.4686 11.7613C13.5277 10.5391 14.1685 8.94459 14.1685 7.20043C14.1685 3.35212 11.0488 0.232422 7.20052 0.232422Z"
                            fill="#9CA3AF"
                          />
                        </svg>
                      </IconButton>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
              {/* </Slide> */}
              {showSearch &&
                <Box className="search-wrapper">
                  <Stack direction="row" alignItems={'center'} spacing={2} sx={{ width: '100%' }}>
                    <TextField id="searching" value={searchValue} inputRef={inputRef} size="small" variant="outlined" placeholder="Enter designation / companies / location" sx={{ background: '#ffffff', borderRadius: '12px', width: '100%' }}
                      onChange={(e) => handleSearchInputChange(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {filterLoading ? <CircularProgress size={20} /> :
                              <IconButton sx={{ border: 'none !important' }} edge="end" size="small" onClick={() => handleSearch(searchValue, filterCriteria)}>
                                <ArrowForwardIcon />
                              </IconButton>
                            }
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Link onClick={handleShowHide} sx={{ 'cursor': 'pointer' }} className='cancel-btn' underline="none">Cancel</Link>
                  </Stack>
                </Box>
              }
            </Box>

            {/* Jobs type toggle */}
            <JobTypeToggle alignment={getToggleAlignment()} handleFetchJobFromJobType={handleFetchJobFromJobType} />
            <Box className="active-jobs-container" ref={scrollContainerRef} sx={{ overflow: 'auto', height: 'calc(100vh - 30vh) !important', paddingBottom: '20vh !important' }}>
              {jobs?.length > 0 &&
                jobs?.map((job, index) => (
                  <Card
                    ref={(el) => (cardRefs.current[job?._id] = el)}
                    className={`job-card-view${(setActiveId && job._id === setActiveId) || (!setActiveId && index === 0) ? ' first-job-item' : ''}`}
                    variant="outlined"
                    sx={{ marginBottom: '12px' }}
                    key={job?._id}
                    tabIndex={0}
                    onClick={() => handleJobData(job)}
                  >
                    {/* card top */}
                    <Box className="job-card-view-top">
                      <Box className="active-jobs-info">
                        {/* card content */}
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={7}>
                            <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                              <Typography className="post-title">{job?.title}</Typography>
                              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" >
                                  <path d="M7.45837 3.39961L12.8917 8.83294C13.5334 9.47461 13.5334 10.5246 12.8917 11.1663L7.45837 16.5996" stroke="#6B7280" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                              </Box>
                            </Stack>
                            {job?.is_company_name_confidential ? <Typography className="company-name">Company Name - Confidential</Typography> : <Typography className="company-name">{job?.company || ''}</Typography>}
                            <Box mt={0.5} className="work-type">
                              <Stack direction="row" flexWrap="wrap" alignItems={'center'} spacing={0.5}>
                                <Typography variant="p" className="city">{job?.location}</Typography>
                                {job?.job_type === 'Full time' && <>
                                  <Typography variant="body1">•</Typography>
                                  <Typography variant="p" className="city">{job?.job_type}</Typography></>}
                                <Typography variant="body1">•</Typography>
                                <Typography variant="p" className="city">{job?.work_mode}</Typography>
                              </Stack>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={5}>
                            {(job?.is_compensation_confidential || !job?.salary) ?
                              <Box className="ctc-range">
                                <Typography className="text-1" >Not Disclosed</Typography>
                                <Typography className="text-2">Compensation</Typography>
                              </Box> :
                              <Box className="ctc-range">
                                <Typography className="text-1">₹{job?.job_type === 'Full time' ? formatNumber(job?.salary) : Intl.NumberFormat('en-IN').format(job?.salary)}</Typography>
                                <Typography className="text-2">{job?.job_type === 'Full time' ? job?.compensation : job?.compensation_model}</Typography>
                              </Box>
                            }
                          </Grid>
                        </Grid>
                        {/* card bottom */}
                        <Box sx={{ marginTop: { xs: '12px', md: '8px' } }}>
                          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent={'space-between'} spacing={{ xs: 2, md: 0 }}>
                            <Typography variant="p" className="posted-date">
                              {job?.updatedAt ? getTimeDifferenceString(job?.updatedAt, "Posted") : 'NA'}
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>
                    </Box>
                    {/* Part time band */}
                    {job?.job_type !== 'Full time' &&
                      <Box className="job-card-view-bottom">
                        <Box mt={0.5} className="work-type">
                          <Stack direction="row" flexWrap="wrap" alignItems={'center'} spacing={0.5}>
                            <Typography variant="p" className="city">{job?.job_type}</Typography>
                            <Typography variant="body1">•</Typography>
                            <Typography variant="p">{job?.weekly_time_commitment} hrs./week</Typography>
                            <Typography variant="body1">•</Typography>
                            <Typography variant="p">For {job?.total_time_commitment_requirement} weeks</Typography>
                          </Stack>
                        </Box>
                      </Box>}
                  </Card>
                ))}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={8} sx={{ display: { xs: 'none', md: 'block' } }}>
          {/* job full details */}
          {/* {setActiveId && userId && (user && jobs && user?.stages > 3)
            ?
            <JobDescription jobId={setActiveId} applyButton={applyButton} setApplyButton={setApplyButton} /> : */}
          {jobs?.length > 0 && setActiveId && !isMobile && <JobDetails jobId={setActiveId} setApplyButton={setApplyButton} applyButton={applyButton} />}
          {/* } */}
        </Grid>
      </Grid>
      {/* filter */}
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={openFilter}
          onClose={() => handlePopoverClose(false)}
          className="mobile-bottomsheet-dialog filter-by-overlay"
        >
          <Box sx={{ height: 400, width: { xs: '100%', md: '600px' } }}>
            <Grid container sx={{ height: '100%' }}>
              <Grid item xs={5} className="filter-by-tabs-item">
                <Tabs
                  orientation="vertical"
                  variant="scrollable"
                  value={value}
                  onChange={handleChange}
                  aria-label="Vertical tabs example"
                  sx={{ height: '100%' }}
                >
                  <Tab label="Sort By" disableRipple {...a11yProps(0)} />
                  <Tab label="Location" disableRipple {...a11yProps(1)} />
                  <Tab label="Job Type" disableRipple {...a11yProps(2)} />
                </Tabs>
              </Grid>

              <Grid item xs={7} className="filter-by-tabs-content">
                <TabPanel value={value} index={0}>
                  <Box>
                    <Typography className="title">Sort By</Typography>
                  </Box>
                  <Box>
                    <FormControl>
                      <RadioGroup
                        aria-labelledby="demo-controlled-radio-1"
                        name="salary"
                        value={sortValue}
                        onChange={handleSortChange}
                      >
                        <FormControlLabel value="1" control={<Radio onDoubleClick={handleDoubleClick} />} label="Compensation (High to Low)" />
                        <FormControlLabel value="0" control={<Radio onDoubleClick={handleDoubleClick} />} label="Compensation (Low to High)" />
                        <FormControlLabel value="11" control={<Radio onDoubleClick={handleDoubleClick} />} label="Posted (Latest First)" />
                        <FormControlLabel value="00" control={<Radio onDoubleClick={handleDoubleClick} />} label="Posted (Oldest First)" />
                      </RadioGroup>
                    </FormControl>
                    <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginTop: '0.5rem', marginBottom: '0.5rem' }} />
                  </Box>
                </TabPanel>

                <TabPanel value={value} index={1}>
                  <Typography className="title">Location</Typography>
                  {preferedLocation && preferedLocation.length > 0 ? (
                    <FormControl>
                      <FormGroup>
                        <Box sx={{ maxHeight: '16rem', overflow: "auto", marginBottom: '16px' }}>
                          <Stack>
                            {[...preferedLocation]?.map((data, index) => (
                              <FormControlLabel
                                key={index}
                                control={
                                  <Checkbox
                                    checked={filterCriteria?.location?.some((fl) => (clusteredData?.find(ct => ct?.value === fl)?.name === data || fl === data))}
                                    onChange={(e) => handleChangeFilterCriteria(e, clusteredData?.find(ct => ct?.name === data)?.value || data)}
                                    name="location"
                                  />
                                }
                                label={data}
                              />
                            ))}
                          </Stack>
                        </Box>
                      </FormGroup>
                    </FormControl>
                  ) : (
                    <Typography>Please log in to view location preferences.</Typography>
                  )}
                </TabPanel>
                <TabPanel value={value} index={2}>
                  <FormControl>
                    <FormGroup>
                      <Typography className="title">Job Type</Typography>
                      {['Full time', 'Part time', 'Temporary', 'Internship']?.map((jobType, index) => (
                        <FormControlLabel
                          key={index}
                          control={
                            <Checkbox
                              checked={filterCriteria?.job_type?.includes(jobType) || false}
                              onChange={(e) => handleChangeFilterCriteria(e, jobType)}
                              name="job_type"
                            />
                          }
                          label={jobType}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </TabPanel>

                <Box className="filter-by-actions-btn">
                  <Stack direction="row" spacing={2}>
                    <Button variant="outlined" fullWidth onClick={() => handlePopoverClose(true)}>{filterApplied ? "Clear Filter" : "Cancel"}</Button>
                    <Button variant="contained" fullWidth onClick={() => handleApplyFilter()} disabled={filterLoading}>{filterLoading ? 'Applying...' : 'Apply'}</Button>
                  </Stack>
                </Box>

              </Grid>
            </Grid>
          </Box>

        </Drawer> :
        <Popover elevation={3}
          id="filter-by"
          className="filter-by-overlay"
          open={openFilter}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClose={() => handlePopoverClose(false)}
          sx={{
            top: '15px',
            left: '-150px'
          }}
          disableRestoreFocus
        >
          <Box
            sx={{
              position: "relative",
              "&::before": {
                backgroundColor: "white",
                content: '""',
                display: "block",
                position: "absolute",
                width: 12,
                height: 12,
                top: -8,
                transform: "rotate(45deg)",
                left: "calc(25% - 7px)",
                borderTop: '1px solid #D2D5DB',
                borderLeft: '1px solid #D2D5DB'
              }
            }}
          />
          <Box sx={{ height: 400, width: 600 }}>
            <Grid container sx={{ height: '100%' }}>
              <Grid item xs={5} className="filter-by-tabs-item">
                <Tabs
                  orientation="vertical"
                  variant="scrollable"
                  value={value}
                  onChange={handleChange}
                  aria-label="Vertical tabs example"
                  sx={{ height: '100%' }}
                >
                  <Tab label="Sort By" disableRipple {...a11yProps(0)} />
                  <Tab label="Location" disableRipple {...a11yProps(1)} />
                  <Tab label="Job Type" disableRipple {...a11yProps(2)} />
                </Tabs>
              </Grid>

              <Grid item xs={7} className="filter-by-tabs-content">
                <TabPanel value={value} index={0}>
                  <Box>
                    <Typography className="title">Sort By</Typography>
                  </Box>
                  <Box>
                    <FormControl>
                      <RadioGroup
                        aria-labelledby="demo-controlled-radio-1"
                        name="salary"
                        value={sortValue}
                        onChange={handleSortChange}
                      >
                        <FormControlLabel value="1" control={<Radio onDoubleClick={handleDoubleClick} />} label="Compensation (High to Low)" />
                        <FormControlLabel value="0" control={<Radio onDoubleClick={handleDoubleClick} />} label="Compensation (Low to High)" />
                        <FormControlLabel value="11" control={<Radio onDoubleClick={handleDoubleClick} />} label="Posted (Latest First)" />
                        <FormControlLabel value="00" control={<Radio onDoubleClick={handleDoubleClick} />} label="Posted (Oldest First)" />
                      </RadioGroup>
                    </FormControl>
                    <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', marginTop: '0.5rem', marginBottom: '0.5rem' }} />
                  </Box>
                </TabPanel>

                <TabPanel value={value} index={1}>
                  <Typography className="title">Location</Typography>
                  {preferedLocation && preferedLocation.length > 0 ? (
                    <FormControl>
                      <FormGroup>
                        <Box sx={{ maxHeight: '16rem', overflow: "auto", marginBottom: '16px' }}>
                          <Stack>
                            {[...preferedLocation]?.map((data, index) => (
                              <FormControlLabel
                                key={index}
                                control={
                                  <Checkbox
                                    checked={filterCriteria?.location?.some((fl) => (clusteredData?.find(ct => ct?.value === fl)?.name === data || fl === data))}
                                    onChange={(e) => handleChangeFilterCriteria(e, clusteredData?.find(ct => ct?.name === data)?.value || data)}
                                    name="location"
                                  />
                                }
                                label={data}
                              />
                            ))}
                          </Stack>
                        </Box>
                      </FormGroup>
                    </FormControl>
                  ) : (
                    <Typography>Please log in to view location preferences.</Typography>
                  )}
                </TabPanel>
                <TabPanel value={value} index={2}>
                  <FormControl>
                    <FormGroup>
                      <Typography className="title">Job Type</Typography>
                      {['Full time', 'Part time', 'Temporary', 'Internship']?.map((jobType, index) => (
                        <FormControlLabel
                          key={index}
                          control={
                            <Checkbox
                              checked={filterCriteria?.job_type?.includes(jobType) || false}
                              onChange={(e) => handleChangeFilterCriteria(e, jobType)}
                              name="job_type"
                            />
                          }
                          label={jobType}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </TabPanel>

                <Box className="filter-by-actions-btn">
                  <Stack direction="row" spacing={2}>
                    <Button variant="outlined" fullWidth onClick={() => handlePopoverClose(true)}>{filterApplied ? "Clear Filter" : "Cancel"}</Button>
                    <Button variant="contained" fullWidth onClick={() => handleApplyFilter()} disabled={filterLoading}>{filterLoading ? 'Applying...' : 'Apply'}</Button>
                  </Stack>
                </Box>

              </Grid>
            </Grid>
          </Box>
        </Popover>
      }
      {/* blur screen  */}
      <Drawer open={filterLoading}>
        <div className="loading-overlay">
          <div className="loading-spinner">
            <CircularProgress />
          </div>
        </div>
      </Drawer>
      {isMobile &&
        <Drawer
          className="mob-side-panel"
          open={openDetailsDrawer}
          anchor={'right'}
          onClose={handleCloseDetailsDrawer}>
          <JobDetailsMobile jobid={jobID} jobId={setActiveId} setApplyButton={setApplyButton} applyButton={applyButton} onClose={handleCloseDetailsDrawer} />
        </Drawer>}
    </Box>
  );
};

export default InternalJobFromExternal;